import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { FASTIFY_BASE_URL } from '../config/apiConfig';
import { useUserSettingsContext } from '../context/UserSettingsContext';

/**
 * The caller's report access for the current org, as resolved by the SAME
 * fastify service that 403s the report data, so nav, catalogs, dashboard
 * sections and the shell's scope chip always agree with enforcement.
 *
 * Primary source: GET /reportData/reportAccess (cached per org for the
 * session). Fallback while loading / on transport failure: the
 * `orgAccess.nativeReports` field stamped onto the initData payload.
 */
const cacheByOrg = new Map(); // organizationId → { groups: array|null, departmentAccess: object|null }

/** Shared fetch/cache state for the hooks below. */
function useReportAccessServerState() {
  const { userSettings, currentOrganization } = useUserSettingsContext();
  const organizationId = currentOrganization?.id;

  // undefined = loading, { groups, departmentAccess } = server answered,
  // { failed } = transport error
  const [serverState, setServerState] = useState(() => cacheByOrg.get(organizationId));

  useEffect(() => {
    let alive = true;
    if (!organizationId) {
      setServerState(undefined);
      return undefined;
    }
    const cached = cacheByOrg.get(organizationId);
    if (cached) {
      setServerState(cached);
      return undefined;
    }
    api
      .get(`${FASTIFY_BASE_URL}/reportData/reportAccess`, { params: { organizationId } })
      .then((res) => {
        const groups = Array.isArray(res.data?.allowedReportGroups) ? res.data.allowedReportGroups : null;
        const departmentAccess =
          res.data?.departmentAccess && typeof res.data.departmentAccess === 'object'
            ? res.data.departmentAccess
            : null;
        const entry = { groups, departmentAccess };
        cacheByOrg.set(organizationId, entry);
        if (alive) setServerState(entry);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          // Denied outright (no org access) → no reports.
          const entry = { groups: [], departmentAccess: null };
          cacheByOrg.set(organizationId, entry);
          if (alive) setServerState(entry);
        } else if (alive) {
          setServerState({ failed: true }); // not cached — retried next mount
        }
      });
    return () => {
      alive = false;
    };
  }, [organizationId]);

  return { serverState, userSettings, currentOrganization };
}

/**
 * Allowed native report GROUPS (Revenue / Operations / Financial) — the
 * user's PBI report permissions mapped server-side.
 * Returns: array = only these groups; [] = no reports; null = unrestricted.
 */
export function useAllowedReportGroups() {
  const { serverState, userSettings, currentOrganization } = useReportAccessServerState();
  const contextValue = userSettings?.orgAccess?.[currentOrganization?.code]?.nativeReports;
  const contextGroups = Array.isArray(contextValue) ? contextValue : null;

  // Server answer is authoritative (including null = unrestricted); the
  // context-stamped value covers loading and transport failure.
  if (serverState && !serverState.failed) return serverState.groups;
  return contextGroups;
}

/**
 * Department-restriction summary for the shell's partial-view scope chip:
 * { coa: { restricted, assignedCount, totalCount, assignedNames },
 *   payroll: { … } } — or null while loading / on transport failure (the
 * chip simply doesn't render until the server answers; there is no
 * client-side fallback because department NAMES only exist server-side).
 */
export function useReportDepartmentAccess() {
  const { serverState } = useReportAccessServerState();
  if (serverState && !serverState.failed) return serverState.departmentAccess || null;
  return null;
}

export function clearReportAccessCache() {
  cacheByOrg.clear();
}

export default useAllowedReportGroups;
